import { ProductVariation } from 'models/productsReferential';
import { useMemo } from 'react';
import { usePinnedProducts } from 'routes/essential-workspace/components/product-selector/hooks/use-pinned';
import { ProductListItem } from 'routes/essential-workspace/components/product-selector/product-list/item';
import { useKeyboardNavigation } from 'routes/essential-workspace/components/product-selector/product-list/use-keyboard-navigation';

function filterBySearch(products: ReadonlyArray<ProductVariation>, search: string) {
  if (!search) {
    return products;
  }

  const normalizedSearch = search.toLowerCase();

  return products.filter(product => {
    const matchesSubType = product.productSubType?.toLowerCase().includes(normalizedSearch);
    const matchesType = product.productType?.toLowerCase().includes(normalizedSearch);
    return matchesSubType || matchesType;
  });
}

type ProductListProps = {
  products: ReadonlyArray<ProductVariation>;
  search: string;
  onSelectProduct: (product: ProductVariation) => void;
};

export function ProductList({ products, search, onSelectProduct }: ProductListProps) {
  const { isProductPinned } = usePinnedProducts('essential');

  const filteredProducts = useMemo(() => filterBySearch(products, search), [products, search]);

  const currentIndex = useKeyboardNavigation({
    search,
    products: filteredProducts,
    onSelect: onSelectProduct,
  });

  if (!filteredProducts.length) {
    return (
      <p className="text-center text-secondary py-3" role="status">
        No matching products found
      </p>
    );
  }

  return (
    <ul className="list-group text-start overflow-y-auto" aria-label="Search product results">
      {filteredProducts.map((product, index) => (
        <ProductListItem
          key={`${product.productType}_${product.productSubType}`}
          product={product}
          isActive={index === currentIndex}
          isPinned={isProductPinned(product)}
          onSelect={onSelectProduct}
        />
      ))}
    </ul>
  );
}
